import { StandardEvents } from '@shopify/events';

/**
 * ROTAS69 option-remote — the generic sibling of assets/r69-size-remote.js for NON-ring options
 * (colour, length, letter, zodiac, …). Drives the main PDP variant option from a compact remote
 * <select> rendered in the sticky add-to-cart bar (snippets/r69-option-remote.liquid). Desktop shows
 * a custom "skin" over the native <select>; mobile shows the native <select>.
 *
 * Keyed by option NAME (data-oname), not a single class, so it works for any option. A remote change
 * drives the main control for that name (radios in buttons/swatch mode, native <select> in dropdown
 * mode); the main picker's `shopify:product:select` re-syncs every remote of the same name. Values
 * are matched by name AND existence: a value chosen on one product only lands on another that also
 * offers it — exactly the ring-size remote's "one value everywhere it exists" behaviour.
 * Morph-resilient: the sticky bar re-renders its children on variant change (strips the JS skin), so
 * a MutationObserver rebuilds it.
 */
(function () {
  if (window.__r69OptRemote) return;
  window.__r69OptRemote = true;

  function attrEsc(v) {
    return String(v).replace(/["\\]/g, '\\$&');
  }
  function onameOf(el) {
    return el.getAttribute('data-oname') || '';
  }
  function nativeOf(el) {
    return el.querySelector('select.r69opt__native');
  }

  /* ---- main control for an option NAME: radios (buttons/swatch) OR a native <select> (dropdown) ---- */
  function mainCtl(oname) {
    var esc = attrEsc(oname);
    var radios = Array.prototype.slice.call(
      document.querySelectorAll('.variant-picker input[type="radio"][data-option-name="' + esc + '"]')
    );
    if (radios.length) {
      // Scope to the FIRST owning fieldset — the main product's control (its variant is the canonical
      // value every remote reflects), matching r69-size-remote's single-main model.
      var host = radios[0].closest('.variant-option');
      var list =
        host &&
        Array.prototype.slice.call(host.querySelectorAll('input[type="radio"][data-option-name="' + esc + '"]'));
      if (!list || !list.length) list = radios;
      return {
        get: function () {
          var r = list.filter(function (x) {
            return x.checked;
          })[0];
          return r ? r.value : (list[0] && list[0].value) || '';
        },
        set: function (v) {
          var r = list.filter(function (x) {
            return x.value === v;
          })[0];
          if (!r || r.checked) return false;
          r.checked = true;
          r.dispatchEvent(new Event('change', { bubbles: true }));
          return true;
        },
      };
    }
    // native <select> (dropdown mode): identified by aria-label === option name
    var sel = document.querySelector('.variant-picker select[aria-label="' + esc + '"]');
    if (sel) {
      return {
        get: function () {
          return sel.value;
        },
        set: function (v) {
          if (sel.value === v) return false;
          sel.value = v;
          sel.dispatchEvent(new Event('change', { bubbles: true }));
          return true;
        },
      };
    }
    return null;
  }

  /* ---- desktop skin over the native <select> ---- */
  function buildSkin(el) {
    var sel = nativeOf(el);
    if (!sel) return;
    var existing = el.querySelector('.r69opt__skin');
    if (existing) existing.remove();

    var skin = document.createElement('div');
    skin.className = 'r69opt__skin';

    var trigger = document.createElement('button');
    trigger.type = 'button';
    trigger.className = 'r69opt__trigger';
    trigger.setAttribute('aria-haspopup', 'listbox');
    trigger.setAttribute('aria-expanded', 'false');
    var cur = document.createElement('span');
    cur.className = 'r69opt__cur';
    cur.textContent = sel.options[sel.selectedIndex] ? sel.options[sel.selectedIndex].textContent : '';
    var caret = document.createElement('span');
    caret.className = 'r69opt__caret';
    caret.setAttribute('aria-hidden', 'true');
    trigger.appendChild(cur);
    trigger.appendChild(caret);

    var panel = document.createElement('div');
    panel.className = 'r69opt__panel';
    panel.hidden = true;
    panel.setAttribute('role', 'listbox');

    Array.prototype.forEach.call(sel.options, function (opt) {
      var b = document.createElement('button');
      b.type = 'button';
      b.className = 'r69opt__opt';
      b.setAttribute('role', 'option');
      b.dataset.value = opt.value;
      b.textContent = opt.textContent;
      if (opt.disabled) b.classList.add('is-soldout');
      if (opt.value === sel.value) b.classList.add('is-selected');
      b.addEventListener('click', function (e) {
        e.preventDefault();
        if (b.classList.contains('is-soldout')) return;
        drive(el, opt.value);
        close();
      });
      panel.appendChild(b);
    });

    skin.appendChild(trigger);
    skin.appendChild(panel);
    el.appendChild(skin);

    // Closed trigger as wide as the open list (measure on a body-parked clone — the hosting section
    // may be content-visibility-skipped at build, where in-place reads return 0). max-width:100% clamps.
    var wProbe = panel.cloneNode(true);
    wProbe.hidden = false;
    wProbe.style.cssText =
      'position:absolute;left:-9999px;top:0;display:block;visibility:hidden;width:max-content;max-height:none;overflow:visible;';
    document.body.appendChild(wProbe);
    var wContent = Math.ceil(wProbe.getBoundingClientRect().width);
    wProbe.parentNode.removeChild(wProbe);
    if (wContent > 0) trigger.style.minWidth = wContent + 16 + 'px';

    function open() {
      if (!panel.hidden) return;
      panel.hidden = false;
      var trect = trigger.getBoundingClientRect();
      var ph = panel.offsetHeight;
      var below = window.innerHeight - trect.bottom;
      var above = trect.top;
      el.classList.toggle('is-up', el.classList.contains('r69opt--sticky') || (below < ph + 12 && above > below));
      trigger.setAttribute('aria-expanded', 'true');
      el.classList.add('r69opt-open');
      document.addEventListener('click', onOutside, true);
    }
    function close() {
      if (panel.hidden) return;
      panel.hidden = true;
      trigger.setAttribute('aria-expanded', 'false');
      el.classList.remove('r69opt-open');
      document.removeEventListener('click', onOutside, true);
    }
    function onOutside(e) {
      if (!el.contains(e.target)) close();
    }
    trigger.addEventListener('click', function (e) {
      e.preventDefault();
      if (panel.hidden) open();
      else close();
    });
  }

  function updateSkin(el) {
    var sel = nativeOf(el);
    var skin = el.querySelector('.r69opt__skin');
    if (!sel || !skin) return;
    var cur = skin.querySelector('.r69opt__cur');
    var lbl = sel.options[sel.selectedIndex] ? sel.options[sel.selectedIndex].textContent : '';
    // only WRITE when changed — a no-op textContent write is still a mutation that re-triggers the
    // sticky-bar MutationObserver and loops forever
    if (cur && cur.textContent !== lbl) cur.textContent = lbl;
    Array.prototype.forEach.call(skin.querySelectorAll('.r69opt__opt'), function (b) {
      b.classList.toggle('is-selected', b.dataset.value === sel.value);
    });
  }

  /* remote change -> reflect locally + drive the main control (fires productSelect -> resyncAll) */
  function drive(el, value) {
    var sel = nativeOf(el);
    if (sel && sel.value !== value) sel.value = value;
    updateSkin(el);
    var ctl = mainCtl(onameOf(el));
    if (ctl) ctl.set(value);
  }

  function resyncAll() {
    Array.prototype.forEach.call(document.querySelectorAll('r69-option-remote'), function (el) {
      var ctl = mainCtl(onameOf(el));
      var sel = nativeOf(el);
      if (!ctl || !sel) return;
      var v = ctl.get();
      // Set only if this remote actually offers the value (cross-product: other products may not have
      // it). Programmatic set does NOT fire change -> no loop.
      if (v && sel.value !== v) {
        var has = Array.prototype.some.call(sel.options, function (o) {
          return o.value === v;
        });
        if (has) sel.value = v;
      }
      updateSkin(el);
    });
  }

  function wire(el) {
    if (!el.__wired) {
      el.__wired = true;
      // delegated so it survives the sticky bar's morph replacing the inner <select>
      el.addEventListener('change', function (e) {
        var t = e.target;
        if (t && t.classList && t.classList.contains('r69opt__native')) drive(el, t.value);
      });
    }
    if (!el.querySelector('.r69opt__skin')) buildSkin(el);
  }

  // Build each remote's skin when it becomes visible (IntersectionObserver), not on a global idle
  // timer. The sticky bar is display:block but parked off-screen via transform until data-stuck, so a
  // page reloaded scrolled down slides it in immediately and the IO fires at once — no 1–2s blank
  // dropdown. Off-screen remotes still build lazily (the perf win the old idle boot was after).
  var skinIO = null;
  function run() {
    if (!skinIO) {
      skinIO = new IntersectionObserver(
        function (entries) {
          entries.forEach(function (e) {
            if (e.isIntersecting) {
              wire(e.target);
              skinIO.unobserve(e.target);
            }
          });
        },
        { rootMargin: '300px' }
      );
    }
    Array.prototype.forEach.call(document.querySelectorAll('r69-option-remote'), function (el) {
      skinIO.observe(el);
    });
  }

  document.addEventListener(StandardEvents.productSelect, function () {
    resyncAll();
    [120, 350, 700].forEach(function (t) {
      setTimeout(resyncAll, t);
    });
  });

  // The sticky bar morphs its children on variant change (strips the JS-built skin); rebuild.
  var mo = new MutationObserver(function () {
    mo.disconnect();
    processRemotes();
    observeSticky();
  });
  function processRemotes() {
    Array.prototype.forEach.call(document.querySelectorAll('r69-option-remote'), function (el) {
      if (!el.__wired || !el.querySelector('.r69opt__skin')) wire(el);
      else updateSkin(el);
    });
  }
  function observeSticky() {
    var bar = document.querySelector('.sticky-add-to-cart__bar');
    if (bar) mo.observe(bar, { childList: true, subtree: true });
  }

  var booted = false;
  function boot() {
    if (booted) return;
    booted = true;
    run();
    observeSticky();
  }
  // Boot ASAP — wiring up the observers is cheap; the skin build itself is visibility-gated (above).
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();
})();
